import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Check } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { PayingForServices } from "../components/site/PayingForServices";
import { PrivatePayRates } from "../components/site/PrivatePayRates";
import { CtaBand } from "../components/site/CtaBand";
import { Reveal } from "../components/site/Motion";
import { SERVICES } from "../data/content";

const ServiceRow = ({ s, flip }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);
  return (
    <article ref={ref} id={s.slug} className="scroll-mt-24 border-t border-[#E2DCD0] py-16 sm:py-24" data-testid={`service-${s.slug}`}>
      <div className="grid items-center gap-10 lg:grid-cols-12 lg:gap-16">
        <Reveal className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
          <div className={`relative aspect-[4/5] overflow-hidden ${flip ? "rounded-[2rem] rounded-tr-[9rem]" : "rounded-[2rem] rounded-tl-[9rem]"}`}>
            <motion.img style={{ y, scale: 1.02 }} src={s.image} alt={s.name} className="h-full w-full object-cover" />
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <Reveal>
            <span className="font-serif-display text-6xl italic text-[#C86D51]">{s.num}</span>
            <h2 className="font-serif-display mt-2 text-5xl leading-none tracking-tight sm:text-6xl">{s.name}</h2>
            <p className="font-serif-display mt-4 text-2xl italic text-[#4A554D]">{s.short}</p>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-[#4A554D] md:text-lg" data-testid={`service-${s.slug}-description`}>{s.description}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <ul className="mt-8 grid gap-3 sm:grid-cols-2">
              {s.points.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-[#1E2621]"><Check className="mt-0.5 h-4 w-4 shrink-0 text-[#A9533A]" />{p}</li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-2" data-testid={`service-${s.slug}-payment`}>
              <span className="font-mono-ui mr-2 text-xs uppercase tracking-[0.18em] text-[#4A554D]">Ways to pay</span>
              {s.pay.map((p) => (
                <span key={p} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold ${s.privateOnly ? "border border-[#F3DBBA] bg-[#FFF8F0] text-[#8A5416]" : "bg-[#E5EBE3] text-[#2C3E33]"}`}>{p}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </article>
  );
};

export default function Services() {
  return (
    <main id="main-content" data-testid="services-page">
      <PageHero
        eyebrow="Services"
        lines={["Support that", <>fits <span className="italic text-[#A9533A]">your</span> recovery.</>]}
        intro="Peer Support, CDCA Counseling and Life Coaching. Individual or group, in-person or by telehealth."
        testid="services-hero"
      />
      <section className="bg-[#FDFBF7]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
          {SERVICES.map((s, i) => <ServiceRow key={s.slug} s={s} flip={i % 2 === 1} />)}
        </div>
      </section>
      <div className="border-t border-[#E2DCD0]"><PayingForServices /></div>
      <PrivatePayRates />
      <CtaBand />
    </main>
  );
}
